/**
 * Select Component JavaScript
 * Arco Design Select Implementation
 */

class SelectComponent {
  constructor(element) {
    this.el = element;
    this.trigger = element.querySelector('.select__trigger');
    this.dropdown = element.querySelector('.select__dropdown');
    this.nativeSelect = element.querySelector('.select__native');
    this.options = element.querySelectorAll('.select__option');
    this.searchInput = element.querySelector('.select__search-input');
    this.emptyState = element.querySelector('.select__empty');
    this.body = element.querySelector('.select__body');
    
    this.isMultiple = element.dataset.selectMultiple === 'true';
    this.isDisabled = element.hasAttribute('data-disabled');
    this.selectedValues = [];
    // Healix-specific: allow API-backed product-card color select to use portal dropdown
    // so it is not clipped by parent containers with overflow constraints.
    this.portalEnabled = this.el.closest('.product-card__variants, .product-card__bundle-variants, .product-card__healix-color');
    this.portalParent = null;
    this.portalNextSibling = null;
    this.handlePortalReposition = () => this.updatePortalPosition();
    
    this.init();
  }
  
  init() {
    if (this.isDisabled) return;
    
    // Initialize selected values from native select
    this.syncFromNative();
    
    // Event listeners
    this.trigger.addEventListener('click', (e) => this.handleTriggerClick(e));
    this.trigger.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    this.options.forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e, option));
    });
    
    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e));
    }
    
    // Close on outside click
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    
    // Tag remove buttons
    this.el.querySelectorAll('.select__tag-remove').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleTagRemove(e));
    });
    
    // Clear button
    const clearBtn = this.el.querySelector('.select__clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => this.handleClear(e));
    }
  }
  
  syncFromNative() {
    this.selectedValues = [];
    const selectedOptions = this.nativeSelect.selectedOptions;
    for (let i = 0; i < selectedOptions.length; i++) {
      const value = selectedOptions[i].value;
      if (value) {
        this.selectedValues.push(value);
      }
    }
    this.updateUI();
  }
  
  handleTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't toggle if clicking tag remove button
    if (e.target.closest('.select__tag-remove') || e.target.closest('.select__clear')) {
      return;
    }
    
    this.toggle();
  }
  
  handleKeydown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggle();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this.isOpen()) {
          this.open();
        }
        this.focusNextOption();
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!this.isOpen()) {
          this.open();
        }
        this.focusPrevOption();
        break;
    }
  }
  
  handleOptionClick(e, option) {
    e.preventDefault();
    e.stopPropagation();
    
    if (option.hasAttribute('aria-disabled')) return;
    
    const value = option.dataset.value;
    
    if (this.isMultiple) {
      this.toggleValue(value);
    } else {
      this.selectValue(value);
      this.close();
    }
  }
  
  handleSearch(e) {
    const query = e.target.value.toLowerCase();
    let hasVisibleOptions = false;
    
    this.options.forEach(option => {
      const label = option.querySelector('.select__option-label').textContent.toLowerCase();
      const isMatch = label.includes(query);
      option.style.display = isMatch ? '' : 'none';
      if (isMatch) hasVisibleOptions = true;
    });
    
    if (this.emptyState) {
      this.emptyState.style.display = hasVisibleOptions ? 'none' : '';
    }
  }
  
  handleOutsideClick(e) {
    if (!this.el.contains(e.target) && !(this.dropdown && this.dropdown.contains(e.target)) && this.isOpen()) {
      this.close();
    }
  }
  
  handleTagRemove(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const tag = e.target.closest('.select__tag');
    if (tag) {
      const value = tag.dataset.value;
      this.deselectValue(value);
    }
  }
  
  handleClear(e) {
    e.preventDefault();
    e.stopPropagation();
    
    this.selectedValues = [];
    this.syncToNative();
    this.updateUI();
    this.dispatchChange();
  }
  
  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }
  
  open() {
    this.el.classList.add('is-open', 'is-focused');
    this.trigger.setAttribute('aria-expanded', 'true');
    
    if (this.portalEnabled) {
      this.mountPortal();
      this.updatePortalPosition();
      this.dropdown.classList.add('select__dropdown--open');
      window.addEventListener('scroll', this.handlePortalReposition, true);
      window.addEventListener('resize', this.handlePortalReposition);
    }
    
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 50);
    }
  }
  
  close() {
    this.el.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    
    if (this.portalEnabled) {
      this.dropdown.classList.remove('select__dropdown--open');
      window.removeEventListener('scroll', this.handlePortalReposition, true);
      window.removeEventListener('resize', this.handlePortalReposition);
      this.unmountPortal();
    }
    
    if (this.searchInput) {
      this.searchInput.value = '';
      this.handleSearch({ target: this.searchInput });
    }
    
    setTimeout(() => {
      if (!this.isOpen()) {
        this.el.classList.remove('is-focused');
      }
    }, 200);
  }
  
  isOpen() {
    return this.el.classList.contains('is-open');
  }

  mountPortal() {
    if (!this.dropdown || this.dropdown.classList.contains('select__dropdown--portal')) return;
    this.portalParent = this.dropdown.parentElement;
    this.portalNextSibling = this.dropdown.nextSibling;
    document.body.appendChild(this.dropdown);
    this.dropdown.classList.add('select__dropdown--portal');
  }

  unmountPortal() {
    if (!this.dropdown || !this.dropdown.classList.contains('select__dropdown--portal')) return;
    if (this.portalParent) {
      if (this.portalNextSibling) {
        this.portalParent.insertBefore(this.dropdown, this.portalNextSibling);
      } else {
        this.portalParent.appendChild(this.dropdown);
      }
    }
    this.dropdown.classList.remove('select__dropdown--portal');
    this.dropdown.style.top = '';
    this.dropdown.style.left = '';
    this.dropdown.style.width = '';
    this.dropdown.style.maxWidth = '';
  }

  updatePortalPosition() {
    if (!this.dropdown || !this.dropdown.classList.contains('select__dropdown--portal')) return;
    const rect = this.trigger.getBoundingClientRect();
    const card = this.el.closest('.product-card');
    const cardRect = card ? card.getBoundingClientRect() : rect;
    const maxWidth = Math.min(rect.width, cardRect.width, window.innerWidth - 16);
    const left = Math.min(Math.max(rect.left, 8), window.innerWidth - maxWidth - 8);
    const offset = 4;
    this.dropdown.style.top = `${rect.bottom + offset}px`;
    this.dropdown.style.left = `${left}px`;
    this.dropdown.style.width = `${maxWidth}px`;
    this.dropdown.style.maxWidth = `${maxWidth}px`;
    this.dropdown.style.minWidth = `${maxWidth}px`;
  }
  
  selectValue(value) {
    this.selectedValues = [value];
    this.syncToNative();
    this.updateUI();
    this.dispatchChange();
  }
  
  toggleValue(value) {
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
    } else {
      this.selectedValues.push(value);
    }
    this.syncToNative();
    this.updateUI();
    this.dispatchChange();
  }
  
  deselectValue(value) {
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
      this.syncToNative();
      this.updateUI();
      this.dispatchChange();
    }
  }
  
  syncToNative() {
    const options = this.nativeSelect.options;
    for (let i = 0; i < options.length; i++) {
      options[i].selected = this.selectedValues.includes(options[i].value);
    }
  }
  
  updateUI() {
    // Update options visual state
    this.options.forEach(option => {
      const value = option.dataset.value;
      const isSelected = this.selectedValues.includes(value);
      
      option.classList.toggle('select__option--selected', isSelected);
      option.setAttribute('aria-selected', isSelected);
      
      // Update checkbox icon for multi-select
      const checkbox = option.querySelector('.select__checkbox');
      if (checkbox) {
        if (isSelected) {
          checkbox.innerHTML = '<svg class="select__check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        } else {
          checkbox.innerHTML = '';
        }
      }
      
      // Update check icon for single select
      if (!this.isMultiple) {
        const existingCheck = option.querySelector('.select__check-icon');
        if (isSelected && !existingCheck) {
          const checkIcon = document.createElement('svg');
          checkIcon.classList.add('select__check-icon');
          checkIcon.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
          checkIcon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
          checkIcon.setAttribute('viewBox', '0 0 24 24');
          checkIcon.setAttribute('fill', 'none');
          checkIcon.setAttribute('stroke', 'currentColor');
          checkIcon.setAttribute('stroke-width', '2');
          option.appendChild(checkIcon);
        } else if (!isSelected && existingCheck) {
          existingCheck.remove();
        }
      }
    });
    
    // Update body content
    this.updateBodyContent();
    
    // Update filled state
    this.el.classList.toggle('select--filled', this.selectedValues.length > 0);
  }
  
  updateBodyContent() {
    if (this.selectedValues.length === 0) {
      // Show placeholder
      const placeholder = this.trigger.querySelector('.select__placeholder');
      if (!placeholder) {
        const existingContent = this.body.querySelector('.select__value, .select__tags');
        if (existingContent) existingContent.remove();
        
        const placeholderSpan = document.createElement('span');
        placeholderSpan.classList.add('select__placeholder');
        placeholderSpan.textContent = this.nativeSelect.options[0]?.textContent || 'Please select';
        this.body.appendChild(placeholderSpan);
      }
    } else if (this.isMultiple) {
      // Show tags
      this.body.innerHTML = '';
      const tagsContainer = document.createElement('span');
      tagsContainer.classList.add('select__tags');
      
      this.selectedValues.forEach(value => {
        const option = Array.from(this.nativeSelect.options).find(opt => opt.value === value);
        if (option) {
          const tag = document.createElement('span');
          tag.classList.add('select__tag');
          tag.dataset.value = value;
          tag.innerHTML = `
            <span class="select__tag-text">${option.textContent}</span>
            <button type="button" class="select__tag-remove" aria-label="Remove ${option.textContent}">
              <svg class="select__tag-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          `;
          
          tag.querySelector('.select__tag-remove').addEventListener('click', (e) => this.handleTagRemove(e));
          tagsContainer.appendChild(tag);
        }
      });
      
      this.body.appendChild(tagsContainer);
    } else {
      // Show single value
      this.body.innerHTML = '';
      const value = this.selectedValues[0];
      const option = Array.from(this.nativeSelect.options).find(opt => opt.value === value);
      
      if (option) {
        const valueSpan = document.createElement('span');
        valueSpan.classList.add('select__value');
        valueSpan.textContent = option.textContent;
        this.body.appendChild(valueSpan);
      }
    }
  }
  
  focusNextOption() {
    const visibleOptions = Array.from(this.options).filter(opt => opt.style.display !== 'none' && !opt.hasAttribute('aria-disabled'));
    const currentIndex = visibleOptions.findIndex(opt => opt === document.activeElement);
    const nextIndex = currentIndex + 1 < visibleOptions.length ? currentIndex + 1 : 0;
    visibleOptions[nextIndex]?.focus();
  }
  
  focusPrevOption() {
    const visibleOptions = Array.from(this.options).filter(opt => opt.style.display !== 'none' && !opt.hasAttribute('aria-disabled'));
    const currentIndex = visibleOptions.findIndex(opt => opt === document.activeElement);
    const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : visibleOptions.length - 1;
    visibleOptions[prevIndex]?.focus();
  }
  
  dispatchChange() {
    const event = new CustomEvent('select:change', {
      bubbles: true,
      detail: {
        name: this.el.dataset.selectName,
        values: this.selectedValues,
        value: this.isMultiple ? this.selectedValues : this.selectedValues[0]
      }
    });
    this.el.dispatchEvent(event);
    
    // Also dispatch native change event on select
    this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// Auto-initialize all select components
function initSelectComponents() {
  document.querySelectorAll('[data-select]').forEach(el => {
    if (!el._selectComponent) {
      el._selectComponent = new SelectComponent(el);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSelectComponents);
} else {
  initSelectComponents();
}

// Re-initialize on Shopify section events
document.addEventListener('shopify:section:load', initSelectComponents);

// Export for external use
window.SelectComponent = SelectComponent;

